import React, { forwardRef } from 'react';
import { BoxProps as MuiBoxProps, Fade as MuiFade } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { extractPounceProps, mapPouncePropsToSx, PounceStyleProps } from '../../utils';
import { Box, BoxProps } from '../Box';

/**
 * The direction from which the FadeIn animation originates
 */
export type FadeDirection = 'top' | 'right' | 'bottom' | 'left' | 'center';

// Props interface
export interface FadeInProps extends Omit<MuiBoxProps, keyof PounceStyleProps>, PounceStyleProps {
  sx?: BoxProps['sx'];

  /** The HTML element or React component to render as the FadeIn container */
  as?: React.ElementType;

  /**
   * The direction from which the component fades in
   *
   * @default 'center'
   */
  from?: FadeDirection;

  /**
   * The duration of the fade in milliseconds
   *
   * @default 300
   */
  duration?: number;

  /**
   * The number in milliseconds to wait before the fading-in begins
   *
   * @default 0
   */
  delay?: number;

  /**
   * Specify the distance (measured in px) from which the animation will initially begin from.
   * This controls how far the element travels during the slide animation.
   *
   * @default 25
   */
  offset?: number;

  /**
   * If specified, controls the transition state explicitly. If undefined, the component
   * will automatically transition in on mount (backward compatible behavior).
   *
   * @default undefined (auto-transitions on mount)
   */
  in?: boolean;

  /**
   * If `true`, the component will unmount from the DOM after it has finished exiting.
   * This can improve performance for expensive component trees by removing them from the DOM
   * when they're not visible.
   *
   * @default false
   */
  unmountOnExit?: boolean;

  /**
   * If `true`, the component will mount when `in` becomes `true`, instead of being mounted
   * immediately. This is useful for lazy mounting components that may be expensive to render.
   *
   * @default false
   */
  mountOnEnter?: boolean;

  /**
   * Controls whether the enter transition should run when the component first mounts.
   * By default, this is automatically determined based on whether the component is controlled:
   * - Uncontrolled mode (in=undefined): appear=true (animate on mount)
   * - Controlled mode (in specified): appear=false (no animation on mount)
   *
   * Set this explicitly to override the default behavior.
   *
   * @default undefined (auto-determined from controlled state)
   */
  appear?: boolean;

  /**
   * Callback fired before the component enters.
   */
  onEnter?: TransitionProps['onEnter'];

  /**
   * Callback fired when the component is entering.
   */
  onEntering?: TransitionProps['onEntering'];

  /**
   * Callback fired when the component has entered.
   */
  onEntered?: TransitionProps['onEntered'];

  /**
   * Callback fired before the component exits.
   */
  onExit?: TransitionProps['onExit'];

  /**
   * Callback fired when the component is exiting.
   */
  onExiting?: TransitionProps['onExiting'];

  /**
   * Callback fired when the component has exited.
   */
  onExited?: TransitionProps['onExited'];

  /**
   * Add a custom transition end trigger. Called with the transitioning DOM node
   * and a done callback. Allows for more fine grained transition end logic.
   */
  addEndListener?: TransitionProps['addEndListener'];

  /** Content to be animated with fade-in effect */
  children: React.ReactNode;
}

/**
 * Calculates the initial transform position based on the `from` direction and `offset` distance.
 * Returns the translate3d transform string that positions the element before it animates into view.
 *
 * Uses translate3d for hardware acceleration and better performance.
 *
 * @param from - The direction from which the element should animate
 * @param offset - The distance in pixels to offset the element
 * @returns CSS transform string for initial position
 */
function getInitialTransform(from: FadeDirection, offset: number): string {
  // Center has no spatial offset, just opacity fade
  if (from === 'center') {
    return 'translate3d(0, 0, 0)';
  }

  // Map direction to transform value
  const transformMap: Record<Exclude<FadeDirection, 'center'>, string> = {
    top: `translate3d(0, -${offset}px, 0)`, // Start above, slide down
    bottom: `translate3d(0, ${offset}px, 0)`, // Start below, slide up
    left: `translate3d(-${offset}px, 0, 0)`, // Start left, slide right
    right: `translate3d(${offset}px, 0, 0)`, // Start right, slide left
  };

  return transformMap[from];
}

export const FadeIn = forwardRef<HTMLElement, FadeInProps>(function FadeIn(
  {
    as = 'div',
    from = 'center',
    duration = 300,
    delay = 0,
    offset = 25,
    in: inProp,
    unmountOnExit = false,
    mountOnEnter = false,
    appear: appearProp,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    addEndListener,
    children,
    sx,
    ...rest
  },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Combine pounce-generated styles with user-provided sx
  const combinedSx = { ...pounceStyles, ...sx };

  // In uncontrolled mode, always show (animate in on mount via callbacks)
  // In controlled mode, respect the provided `in` prop
  const isControlled = inProp !== undefined;
  const isIn = inProp ?? true;

  // Determine appear behavior: if not explicitly set, animate on mount in uncontrolled mode
  const shouldAppear = appearProp ?? !isControlled;

  // Callback handlers manage transform animations via direct DOM manipulation
  const handleEnter = React.useCallback(
    (node: HTMLElement, isAppearing: boolean) => {
      if (from !== 'center') {
        // Set initial transform position (offset from final position)
        // eslint-disable-next-line no-param-reassign
        node.style.transform = getInitialTransform(from, offset);
      }

      // Call user's onEnter callback if provided
      if (onEnter) {
        onEnter(node, isAppearing);
      }
    },
    [from, offset, onEnter],
  );

  const handleEntering = React.useCallback(
    (node: HTMLElement, isAppearing: boolean) => {
      if (from !== 'center') {
        // Set up transition for transform (MUI Fade handles opacity)
        const transformTrans = `transform ${duration}ms ease-in-out ${delay}ms`;
        const currentTransition = node.style.transition;
        // eslint-disable-next-line no-param-reassign
        node.style.transition = currentTransition
          ? `${currentTransition}, ${transformTrans}`
          : transformTrans;

        // Trigger reflow to ensure the initial transform is applied before animating
        // eslint-disable-next-line
        node.offsetHeight;

        // Apply final transform to trigger animation
        // eslint-disable-next-line no-param-reassign
        node.style.transform = 'translate3d(0, 0, 0)';
      }

      // Call user's onEntering callback if provided
      if (onEntering) {
        onEntering(node, isAppearing);
      }
    },
    [from, duration, delay, onEntering],
  );

  const handleExit = React.useCallback(
    (node: HTMLElement) => {
      if (from !== 'center') {
        // Set up transition for exit animation
        const transformTrans = `transform ${duration}ms ease-in-out ${delay}ms`;
        const currentTransition = node.style.transition;
        // eslint-disable-next-line no-param-reassign
        node.style.transition = currentTransition
          ? `${currentTransition}, ${transformTrans}`
          : transformTrans;

        // Animate back to offset position on exit
        // eslint-disable-next-line no-param-reassign
        node.style.transform = getInitialTransform(from, offset);
      }

      // Call user's onExit callback if provided
      if (onExit) {
        onExit(node);
      }
    },
    [from, duration, delay, offset, onExit],
  );

  // MUI Fade handles opacity while callbacks handle transform animations
  return (
    <MuiFade
      in={isIn}
      timeout={duration}
      appear={shouldAppear}
      unmountOnExit={unmountOnExit}
      mountOnEnter={mountOnEnter}
      style={delay > 0 ? { transitionDelay: `${delay}ms` } : undefined}
      onEnter={handleEnter}
      onEntering={handleEntering}
      onEntered={onEntered}
      onExit={handleExit}
      onExiting={onExiting}
      onExited={onExited}
      addEndListener={addEndListener}
    >
      <Box ref={ref} as={as} sx={combinedSx} {...otherProps}>
        {children}
      </Box>
    </MuiFade>
  );
});

export default FadeIn;
